'use client';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { ColDef, GridApi } from 'ag-grid-community';
import { token } from '@terra/ui-styled-system';

type UseAutoPinActionsColumnProps = {
  enabled: boolean;
  gridApi: GridApi | null;
  gridContainerRef: RefObject<HTMLElement | null>;
};

export const ACTIONS_COLUMN_ID = 'actions';

export function useAutoPinActionsColumn({
  enabled,
  gridApi,
  gridContainerRef,
}: UseAutoPinActionsColumnProps) {
  const resizeHandlerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollHolderRef = useRef<HTMLElement | null>(null);
  const scrollableContentRef = useRef<HTMLElement | null>(null);
  const pinnedHeaderRef = useRef<HTMLElement | null>(null);
  const pinnedContainerRef = useRef<HTMLElement | null>(null);

  const checkHorizontalScroll = useCallback(() => {
    if (
      enabled &&
      scrollHolderRef.current &&
      scrollableContentRef.current &&
      pinnedContainerRef.current &&
      pinnedHeaderRef.current
    ) {
      const hasScroll =
        scrollHolderRef.current.scrollWidth >
        scrollHolderRef.current.clientWidth;

      if (hasScroll) {
        const isScrolledToRight =
          scrollHolderRef.current.scrollLeft +
            scrollHolderRef.current.clientWidth >=
          scrollHolderRef.current.scrollWidth;
        const leftBoxShadow = isScrolledToRight
          ? ''
          : `-4px 0 8px rgba(10, 10, 10, 0.1), -1px 0 0 ${token('colors.border.neutral.default')}`;

        pinnedContainerRef.current.style.boxShadow = leftBoxShadow;
        pinnedHeaderRef.current.style.boxShadow = leftBoxShadow;
      } else {
        pinnedContainerRef.current.style.boxShadow = '';
        pinnedHeaderRef.current.style.boxShadow = '';
      }
    }
  }, [enabled]);

  const onGridReadyHandler = useCallback(
    (api: GridApi) => {
      if (!enabled || !gridContainerRef.current) return;

      scrollHolderRef.current = gridContainerRef.current.querySelector(
        '.ag-center-cols-viewport',
      ) as HTMLElement | null;
      scrollableContentRef.current = gridContainerRef.current.querySelector(
        '.ag-center-cols-container',
      ) as HTMLElement | null;
      pinnedHeaderRef.current = gridContainerRef.current.querySelector(
        '.ag-pinned-right-header',
      ) as HTMLElement | null;
      pinnedContainerRef.current = gridContainerRef.current.querySelector(
        '.ag-pinned-right-cols-container',
      ) as HTMLElement | null;

      if (pinnedHeaderRef.current) {
        pinnedHeaderRef.current.style.borderLeft = 'none';
      }

      checkHorizontalScroll();

      const actionsColumn = api.getColumn(ACTIONS_COLUMN_ID);
      if (actionsColumn) {
        api.autoSizeColumns([ACTIONS_COLUMN_ID]);
      }
    },
    [enabled, checkHorizontalScroll, gridContainerRef],
  );

  const getColumnDefsWithActionsColumn = useCallback(
    (columnDefs: ColDef[]): ColDef[] => {
      return columnDefs?.map((col) => ({
        ...col,
        ...(col?.colId === ACTIONS_COLUMN_ID && {
          pinned: 'right',
          suppressMovable: true,
          resizable: false,
          filter: false,
          cellStyle: { borderLeft: 'none' },
        }),
      }));
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled || !gridApi) return;

    const resizeHandler = () => {
      if (resizeHandlerTimeoutRef.current) {
        clearTimeout(resizeHandlerTimeoutRef.current);
      }

      resizeHandlerTimeoutRef.current = setTimeout(
        () => checkHorizontalScroll(),
        10,
      );
    };

    scrollHolderRef.current?.addEventListener('scroll', checkHorizontalScroll);
    window.addEventListener('resize', resizeHandler);

    return () => {
      scrollHolderRef.current?.removeEventListener(
        'scroll',
        checkHorizontalScroll,
      );
      window.removeEventListener('resize', resizeHandler);
      if (resizeHandlerTimeoutRef.current) {
        clearTimeout(resizeHandlerTimeoutRef.current);
        resizeHandlerTimeoutRef.current = null;
      }
    };
  }, [enabled, checkHorizontalScroll, gridApi]);

  return {
    checkHorizontalScroll,
    onGridReadyHandler,
    getColumnDefsWithActionsColumn,
  };
}
